import { ReactNode, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { Loading } from "@/components/ui/loading";
import { useAppSelector } from "@/hooks/redux";
import { usePermission } from "@/hooks/userPermission";
import { EmployerPermissions } from "@/types/user";
import { hasPermission } from "@/utils/permission";

interface RequireEmployerPermissionProps {
  children?: ReactNode;
  permission: EmployerPermissions;
  permissionName: string;
  redirectTo?: string;
}

export default function RequireEmployerPermission({
  children,
  permission,
  permissionName,
  redirectTo = "/employer",
}: RequireEmployerPermissionProps) {
  const navigate = useNavigate();
  const { notPermitted } = usePermission();
  const user = useAppSelector((state) => state.auth.userData?.user);

  const isUnauthorized =
    user?.type === "employer" && !hasPermission(permission, user.permissions);

  useEffect(() => {
    if (!isUnauthorized) return;

    notPermitted({ permissionName });
    navigate(redirectTo, { replace: true });
  }, [isUnauthorized, navigate, notPermitted, permissionName, redirectTo]);

  if (!user) {
    return <Loading />;
  }

  if (isUnauthorized) {
    return null;
  }

  return <>{children ?? <Outlet />}</>;
}
